/**
 * Event envelopes (F6; firehose §3.1; ADR-12 signing). Build path signs, then
 * validates the JSON model with @beacon/lexicon before returning (`sig` is a
 * required envelope field, so validation needs it) — a client never emits an
 * event the catalog rejects. Parse path is the mirror: strict decode → lexicon
 * → wire-header consistency → signature (best-effort: the relay A1 is the
 * authoritative ingress verifier; subscribers verify when they can resolve the
 * signer — ADR-12 resolution-is-a-fold).
 */
import type { Clock } from "@beacon/clock";
import {
  dagCborEncode,
  signEnvelope,
  verifyEnvelope,
  type CborValue,
} from "@beacon/crypto";
import { validateEvent, type FieldError } from "@beacon/lexicon";
import type { FirehoseEvent } from "@beacon/transport";
import { boxFloats } from "./box-floats.js";
import { dagCborDecode } from "./cbor-decode.js";
import { rfc3339FromMicros, stdB64Encode, toCborModel, toJsonModel } from "./model.js";
import type { GeoJson } from "./geo.js";
import type { IdentityKeys } from "./identity.js";

export interface EventInput {
  $type: string;
  payload: unknown;        // JSON model (sealed fields as {ciphertext:{$bytes},…})
  geo: GeoJson;
  subject?: string;
}

export interface SignedEnvelope {
  json: Record<string, unknown>;          // JSON model incl. sig {$bytes}
  cbor: Record<string, CborValue>;        // CBOR form incl. sig Uint8Array
  wire: FirehoseEvent;
}

function formatFieldErrors(errors: FieldError[]): string {
  return errors.map((e) => `${e.path}: ${e.message}`).join("; ");
}

export class LexiconError extends Error {
  constructor(public readonly errors: FieldError[]) {
    super(`lexicon validation failed: ${formatFieldErrors(errors)}`);
    this.name = "LexiconError";
  }
}

type Signer = Pick<IdentityKeys, "did" | "signingKey" | "signingPrivate">;

export async function buildEnvelope(
  input: EventInput,
  signer: Signer,
  clock: Clock,
): Promise<SignedEnvelope> {
  const id = clock.tid();
  const unsigned: Record<string, unknown> = {
    id,
    $type: input.$type,
    identityId: signer.did,
    signingKey: signer.signingKey,
    createdAt: rfc3339FromMicros(clock.now()),
    geo: input.geo,
    ...(input.subject !== undefined ? { subject: input.subject } : {}),
    payload: input.payload,
  };
  const cborUnsigned = boxFloats(toCborModel(unsigned) as Record<string, CborValue>, input.$type);
  const sig = await signEnvelope(cborUnsigned, signer.signingPrivate);
  const json = { ...unsigned, sig: { $bytes: stdB64Encode(sig) } };
  const res = validateEvent(json);
  if (!res.ok) throw new LexiconError(res.errors);
  const cbor: Record<string, CborValue> = { ...cborUnsigned, sig };
  return {
    json,
    cbor,
    wire: {
      id,
      type: input.$type,
      cells: [input.geo.cell, ...input.geo.path],
      bytes: dagCborEncode(cbor),
    },
  };
}

export type ParseOk = {
  ok: true;
  json: Record<string, unknown>;
  cbor: Record<string, CborValue>;
  /** true = signature checked against the resolver's chain head; false = signer unresolvable (relay-trusted). */
  verified: boolean;
};
export type ParseFail = {
  ok: false;
  /** "stale-key" = signed by a rotated-away key (re-resolve the signer); "bad-sig" = crypto verification failed (reject). */
  reason: "decode" | "shape" | "lexicon" | "unknown-type" | "header-mismatch" | "stale-key" | "bad-sig";
  detail?: string;
};
export type ParseResult = ParseOk | ParseFail;

export interface ParseOptions {
  /** did → current signing did:key, or null when unknown (IdentityDirectory.resolve). */
  resolveSigningKey?: (did: string) => string | null | Promise<string | null>;
  /** Reject events whose $type the catalog doesn't know (default false: open vocab, forward compat). */
  rejectUnknownTypes?: boolean;
}

export type VerifyOutcome =
  | { ok: true; verified: boolean }
  | { ok: false; reason: "stale-key" | "bad-sig" };

/**
 * Signature stage of parseWire, exported for two-phase consumers:
 * subscription.ts runs the structural stage (parseWire without a resolver),
 * ingests identity events into the IdentityDirectory, THEN verifies — so a
 * genesis event arriving on the stream can verify itself (ADR-12).
 * Expects a structurally valid envelope (parseWire's ok=true `cbor`).
 */
export async function verifyParsed(
  cbor: Record<string, CborValue>,
  resolveSigningKey?: ParseOptions["resolveSigningKey"],
): Promise<VerifyOutcome> {
  if (!resolveSigningKey) return { ok: true, verified: false };
  const head = await resolveSigningKey(cbor.identityId as string);
  if (head === null) return { ok: true, verified: false };
  // ADR-12 ingress rule: verify against the CURRENT chain head, not the
  // envelope's self-declared signingKey — rotated-away keys must fail.
  if (cbor.signingKey !== head) return { ok: false, reason: "stale-key" };
  if (!(await verifyEnvelope(cbor, head))) return { ok: false, reason: "bad-sig" };
  return { ok: true, verified: true };
}

export async function parseWire(evt: FirehoseEvent, opts: ParseOptions = {}): Promise<ParseResult> {
  let cbor: CborValue;
  try {
    cbor = dagCborDecode(evt.bytes);
  } catch (e) {
    return { ok: false, reason: "decode", detail: String(e) };
  }
  if (typeof cbor !== "object" || cbor === null || Array.isArray(cbor) || cbor instanceof Uint8Array) {
    return { ok: false, reason: "shape", detail: "envelope is not a map" };
  }
  const json = toJsonModel(cbor) as Record<string, unknown>;
  const res = validateEvent(json);
  if (!res.ok) return { ok: false, reason: "lexicon", detail: formatFieldErrors(res.errors) };
  if (!res.known && opts.rejectUnknownTypes) {
    return { ok: false, reason: "unknown-type", detail: String(json.$type) };
  }
  const geo = json.geo as { cell: string; path: string[] };
  const wantCells = [geo.cell, ...geo.path];
  const mismatched: string[] = [];
  if (evt.id !== json.id) mismatched.push("id");
  if (evt.type !== json.$type) mismatched.push("type");
  if (evt.cells.length !== wantCells.length || evt.cells.some((c, i) => c !== wantCells[i])) {
    mismatched.push("cells");
  }
  if (mismatched.length > 0) {
    return {
      ok: false,
      reason: "header-mismatch",
      detail: `wire header disagrees with signed envelope: ${mismatched.join(", ")}`,
    };
  }
  const v = await verifyParsed(cbor as Record<string, CborValue>, opts.resolveSigningKey);
  if (!v.ok) return v;
  return { ok: true, json, cbor: cbor as Record<string, CborValue>, verified: v.verified };
}
